import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowRight, Check, AlertCircle, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PARTNER_PACKAGES } from '../config/packages';

const PartnerAboPage = () => {
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get('cancelled');
  const planParam = searchParams.get('plan') || 'pro';
  
  const [selectedPlan, setSelectedPlan] = useState(planParam);
  
  // Sync selectedPlan with URL parameter
  useEffect(() => {
    const urlPlan = searchParams.get('plan');
    if (urlPlan && ['starter', 'pro', 'premium'].includes(urlPlan)) {
      setSelectedPlan(urlPlan);
    }
  }, [searchParams]);
  
  const [formData, setFormData] = useState({
    company_name: '',
    contact_first_name: '',
    contact_last_name: '',
    email: '',
    phone: '',
    agreements_accepted: false,
    privacy_accepted: false,
    data_permission_accepted: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.contact_first_name || !formData.contact_last_name || !formData.email) {
      setError('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    
    if (!formData.agreements_accepted || !formData.privacy_accepted || !formData.data_permission_accepted) {
      setError('Bitte akzeptieren Sie alle Bedingungen');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stripe/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email,
          origin_url: window.location.origin,
          plan_tier: selectedPlan,
          metadata: {
            company_name: formData.company_name,
            contact_first_name: formData.contact_first_name,
            contact_last_name: formData.contact_last_name,
            phone: formData.phone,
            plan_tier: selectedPlan
          }
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Checkout fehlgeschlagen');
      }

      // Redirect to Stripe
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Abo abschließen | Dein Gutachten Direkt Partner</title>
        <meta name="description" content="Schließe dein Partner-Abo ab und erhalte sofort Zugang zum Portal." />
      </Helmet>
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          
          {/* Cancelled Info Box */}
          {cancelled === 'true' && (
            <div className="mb-8 p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-1">Zahlung abgebrochen</h3>
                  <p className="text-sm text-blue-800">
                    Es wurde nichts berechnet. Du kannst jederzeit erneut starten.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="text-center mb-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
              Abo abschließen
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Schließe dein Partner-Abo ab und erhalte sofort Zugang zum Portal.
            </p>
            <p className="text-sm text-gray-500">
              Bereits Partner? 
              <a href="/portal/login" className="text-brand-green hover:text-brand-green-dark font-semibold ml-2 underline">
                Hier einloggen
              </a>
            </p>
          </div>

          {/* Selected Package Summary */}
          {(() => {
            const selectedPackage = PARTNER_PACKAGES.find(p => p.id === selectedPlan);
            return (
              <div className="mb-8 p-6 bg-gradient-to-br from-brand-green/5 to-brand-blue/5 rounded-2xl border-2 border-brand-green/20">
                <h3 className="font-semibold text-gray-900 mb-3">Gewähltes Paket:</h3>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Paket</div>
                    <div className="text-lg font-bold text-gray-900">{selectedPackage?.name}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Preis</div>
                    <div className="text-lg font-bold text-gray-900">{selectedPackage?.priceDisplay} / Monat</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Provision</div>
                    <div className="text-lg font-bold text-brand-green">{selectedPackage?.commissionDisplay} vom Grundhonorar</div>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Du kannst später im Portal dein Paket wechseln oder kündigen. Upgrade wirkt sofort, Downgrade zum nächsten Abrechnungszeitpunkt.
                </p>
              </div>
            );
          })()}

          {/* Package Selection */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            {PARTNER_PACKAGES.map((pkg) => {
              const isSelected = selectedPlan === pkg.id;
              
              return (
                <button
                  key={pkg.id}
                  onClick={() => setSelectedPlan(pkg.id)}
                  type="button"
                  className={`relative p-6 rounded-2xl border-2 transition-all text-left ${
                    isSelected
                      ? 'border-brand-green bg-brand-green/5 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <div className="px-3 py-1 bg-brand-green text-white text-xs font-semibold rounded-full flex items-center gap-1">
                        <Zap className="h-3 w-3" />
                        {pkg.badge}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">{pkg.name}</h3>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-brand-green bg-brand-green' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="text-3xl font-bold text-gray-900">{pkg.priceDisplay}</div>
                    <div className="text-sm text-gray-600">pro Monat</div>
                  </div>
                  
                  <div className="inline-block px-3 py-1 bg-brand-green/10 text-brand-green text-sm font-semibold rounded-lg">
                    {pkg.commissionDisplay} Provision
                  </div>
                </button>
              );
            })}
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-3xl p-10 lg:p-12 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Firmenname */}
              <div>
                <label htmlFor="company_name" className="block text-sm font-semibold text-gray-900 mb-3">
                  Firmenname *
                </label>
                <input
                  type="text"
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green text-base"
                  placeholder="z.B. Autohaus Schmidt GmbH"
                  required
                />
              </div>

              {/* Ansprechpartner */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-semibold text-gray-900 mb-3">
                    Vorname *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    value={formData.contact_first_name}
                    onChange={(e) => setFormData({ ...formData, contact_first_name: e.target.value })}
                    className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green text-base"
                    placeholder="Max"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm font-semibold text-gray-900 mb-3">
                    Nachname *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    value={formData.contact_last_name}
                    onChange={(e) => setFormData({ ...formData, contact_last_name: e.target.value })}
                    className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green text-base"
                    placeholder="Mustermann"
                    required
                  />
                </div>
              </div>

              {/* E-Mail */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-3">
                  E-Mail-Adresse *
                </label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green text-base"
                  placeholder="info@autohaus-schmidt.de"
                  required
                />
              </div>

              {/* Telefon */}
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-3">
                  Telefonnummer (optional)
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-5 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-green text-base"
                  placeholder="+49 170 1234567"
                />
              </div>

              {/* Checkboxen */}
              <div className="space-y-4 pt-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.agreements_accepted}
                    onChange={(e) => setFormData({ ...formData, agreements_accepted: e.target.checked })}
                    className="mt-1 w-5 h-5 text-brand-green border-gray-300 rounded focus:ring-brand-green"
                  />
                  <span className="text-sm text-gray-700">
                    Ich habe die <a href="/partnerbedingungen" target="_blank" className="text-brand-green hover:text-brand-green-dark font-semibold">Partnerbedingungen v1.0</a> gelesen und akzeptiere diese.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.data_permission_accepted}
                    onChange={(e) => setFormData({ ...formData, data_permission_accepted: e.target.checked })}
                    className="mt-1 w-5 h-5 text-brand-green border-gray-300 rounded focus:ring-brand-green"
                  />
                  <span className="text-sm text-gray-700">
                    Ich bestätige, dass ich berechtigt bin, die übermittelten Daten und Fotos an Dein-Gutachten-Direkt weiterzugeben.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.privacy_accepted}
                    onChange={(e) => setFormData({ ...formData, privacy_accepted: e.target.checked })}
                    className="mt-1 w-5 h-5 text-brand-green border-gray-300 rounded focus:ring-brand-green"
                  />
                  <span className="text-sm text-gray-700">
                    Ich habe die <a href="/datenschutz" target="_blank" className="text-brand-green hover:text-brand-green-dark font-semibold">Datenschutzerklärung</a> gelesen und akzeptiere diese.
                  </span>
                </label>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                size="lg"
                className="w-full bg-brand-green hover:bg-brand-green-dark text-white px-8 py-6 rounded-xl font-semibold text-lg"
              >
                {loading ? 'Weiterleitung zu Stripe...' : `Jetzt ${PARTNER_PACKAGES.find(p => p.id === selectedPlan)?.name} kostenpflichtig abonnieren`}
                <ArrowRight className="w-5 h-5 ml-3" />
              </Button>

              <p className="text-xs text-gray-500 text-center">
                Nach Klick werden Sie zu Stripe weitergeleitet. Ihre Zahlung ist sicher.
              </p>
            </form>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PartnerAboPage;
